import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Plus } from "lucide-react";
import PageHeader from "../../../components/layout/PageHeader";
import { transitionState } from "../../../components/layout/PageTransition";
import {
  Button,
  EmptyState,
  SkeletonCard,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import SendSupplementModal from "../components/SendSupplementModal";
import SupplementCard from "../components/SupplementCard";
import {
  useSupplementPlan,
  useSupplementPlanWrites,
} from "../hooks/useSupplementPlans";
import { useSupplementUnits } from "../hooks/useSupplementUnits";
import { useMealSlotTypes } from "../../mealPlans/hooks/useMealSlotTypes";
import {
  createSupplementDraft,
  isSameSupplement,
  isSupplementComplete,
  isSupplementWritten,
  toSupplementDraft,
} from "../supplementMapping";
import { useClient } from "../../clients/hooks/useClient";
import { toClientDetail } from "../../clients/clientMapping";
import {
  lockedProps,
  useSubscriptionGate,
} from "../../billing/hooks/useSubscriptionGate";
import {
  ROUTES,
  clientDetailPath,
  createSupplementPath,
} from "../../../routes/paths";
import { ApiError } from "../../../lib/http";
import { decodeRouteId } from "../../../routes/routeIds";
import type {
  PlanSupplement,
  SupplementDraft,
  SupplementPlanRecord,
} from "../types/supplements.types";

/** The client's initial, in the disc the strip leads with. */
const initialOf = (name: string): string =>
  name.trim().charAt(0).toUpperCase() || "?";

/**
 * Writing a client's supplements: one card per supplement, each with its
 * dose, its days and the times it is taken.
 *
 * A screen rather than a dialog over the table, unlike the exercise plan's.
 * A supplement carries a schedule and a row per dose, and several are written
 * in one sitting — that is a page of forms, not something to read through a
 * panel it is drawn on top of.
 *
 * It opens on a blank card, or on a plan that already exists — see `?plan=`
 * on `createSupplementPath`, which the panel's rows link to. An existing plan
 * is fetched whole from `GET /api/v1/supplement-plans/{public_id}/` and its
 * supplements open as cards to read with a Modify on each, the way the meal
 * plan builder opens a written option.
 *
 * The plan is written the way a meal plan is: card by card, as each one's
 * Save is pressed. The first Save on a blank screen is the `POST` that makes
 * the plan, and every Save after it — and every card removed — is a `PUT`,
 * which is a replace: the whole list goes up and the plan ends up holding
 * exactly that. So by the time anything is sent, the backend already holds
 * it, and Save as draft has nothing to write — it is the way out that leaves
 * the plan as a draft, the same as the meal builder's. Leaving by any other
 * door writes whatever was still open — see `autosave`.
 *
 * Two catalogs sit behind the cards. A supplement is stored against a unit's
 * id and each of its doses against a meal's, so `/api/v1/supplement-units/`
 * and `/api/v1/meal-slot-types/` are both fetched here, once for the page, and
 * a blank card fills itself in from them as they answer.
 */
const CreateSupplementPage: React.FC = () => {
  // The param is the enciphered token `createSupplementPath` wrote, not the id
  // itself. A token that does not decode comes back as "", which `useClient`
  // already reports as a missing client.
  const { clientId: token = "" } = useParams();
  const clientId = decodeRouteId(token);

  const navigate = useNavigate();
  const toast = useToast();
  const gate = useSubscriptionGate();
  const saveGate = gate.props("save a supplement");

  const { client: record, status, reload } = useClient(clientId);
  const { options: units } = useSupplementUnits();
  const { options: meals } = useMealSlotTypes();
  const { create, save, send } = useSupplementPlanWrites(clientId);

  // Which plan the screen was opened on, if any. Written into the URL by the
  // first card saved on a blank screen too — see `handleSaveCard` — so a
  // reload lands back on the plan that was started rather than on a blank
  // screen that would start a second one.
  const [params] = useSearchParams();
  const openedOn = decodeRouteId(params.get("plan") ?? "");

  // Whether the screen was opened blank, as of the way in. Held rather than
  // read off the URL, which the first save rewrites: a screen that opened to
  // create is still the create screen once it has, not one to modify.
  const openedBlank = useRef(!openedOn).current;

  /**
   * Where to go back to, when the list that opened this screen said.
   *
   * Router state rather than the URL: the client's Plans tab is the right way
   * back from itself, and the Create plan screen's Supplement list is the
   * right way back from there — and only the list that opened the screen
   * knows which. State is dropped on a reload and a shared link, and what is
   * left is the default below, which is always somewhere the plan is listed.
   */
  const { state } = useLocation();
  const returnTo =
    typeof (state as { returnTo?: unknown } | null)?.returnTo === "string"
      ? (state as { returnTo: string }).returnTo
      : null;
  /**
   * Whether the plan this opened on was made by copying another one, a moment
   * ago — set by the row menu's Duplicate. It only changes what the screen is
   * called: a copy is a draft like any other, and everything on it is edited
   * the same way.
   *
   * Router state, like `returnTo`, and dropped on a reload for the same
   * reason: by then it is simply a draft being worked on, and the heading
   * falls back to Modify.
   */
  const duplicated =
    (state as { duplicated?: unknown } | null)?.duplicated === true;
  const { plan, status: planStatus } = useSupplementPlan(openedOn);

  /**
   * The plan as the backend holds it — fetched, on a screen opened on one,
   * and made by the first card saved on one opened blank. Undefined until
   * either: a blank screen has no plan behind it until a card is saved.
   *
   * What every write after the first goes to, and what the send dialog
   * renders its Download from. Moved on by hand as each write answers rather
   * than read off the query, which knows nothing of a plan this screen made.
   */
  const [stored, setStored] = useState<SupplementPlanRecord>();

  /**
   * What the backend holds for each card, by the card's id — what a card
   * reopened with Modify goes up as while it is still being changed, and what
   * the leave-flush measures an open card against. A ref rather than state
   * because nothing on screen is drawn from it.
   */
  const storedCards = useRef(new Map<string, PlanSupplement>());

  /**
   * Whether the plan is only to be read.
   *
   * An active plan — `sent`, in this module's vocabulary — is the one the
   * client is taking, and changing it under them is not something this screen
   * offers: it is issued, not still being written. So every control that
   * writes goes away rather than greying out — Modify and the bin on each
   * card, Save as draft, and the button that adds a card. What is left is the
   * plan as it was sent, and one way to send it again. The same reasoning the
   * meal builder follows for an activated plan.
   */
  const readOnly = stored?.status === "sent";

  /**
   * The cards on screen.
   *
   * A blank card until the plan arrives, and the plan's own supplements once
   * it has. Filled in by the effect below rather than in the initialiser,
   * because the plan is fetched: there is nothing to open on for the first
   * render of a screen that was opened on one.
   *
   * Once filled they are the form's own state, and the effect does not run
   * again — a card that re-seeded itself from the response on every render
   * would throw away whatever had been typed into it.
   */
  const [drafts, setDrafts] = useState<SupplementDraft[]>(() => [
    createSupplementDraft(),
  ]);

  // Whether the cards have been filled from a plan — fetched, or made here.
  // Set by the first save as well as by the effect: the save writes the plan
  // into the URL, the query then fetches it, and that answer is not to land
  // on top of the cards it was written from.
  const filled = useRef(false);

  useEffect(() => {
    if (filled.current || !plan) return;
    filled.current = true;

    setStored(plan);
    storedCards.current = new Map(
      plan.supplements.map((supplement) => [supplement.id, supplement])
    );
    // A plan with nothing on it still opens on a card, so the screen is one to
    // write in rather than an empty page.
    setDrafts(
      plan.supplements.length
        ? plan.supplements.map(toSupplementDraft)
        : [createSupplementDraft()],
    );
  }, [plan]);

  /**
   * A card with no unit takes the first its catalog offers — once that
   * catalog has answered.
   *
   * A new card opens without one: the factory cannot guess at a unit the
   * backend may not hold, and it is sent as a catalog id. So this is what
   * fills it in, here and for every card added later.
   *
   * Blanks only. A unit that was picked is left exactly as it is — and a
   * catalog that never answers leaves the field empty rather than putting a
   * word behind it that the backend has never heard of. `isSupplementComplete`
   * is what stops such a card being saved, so the failure is a Save that will
   * not go rather than a request that is refused.
   *
   * The dose rows are not touched. They used to take the first meal the same
   * way, and opened at eight before it — so a card saved untouched went up
   * with a schedule nobody chose. A row opens on its placeholders now and is
   * the dietitian's to write.
   */
  useEffect(() => {
    const unit = units[0];
    if (!unit) return;

    setDrafts((current) => {
      let changed = false;

      const filled = current.map((draft) => {
        if (draft.unit) return draft;
        changed = true;
        return { ...draft, unit: unit.name, unitId: unit.id };
      });

      // The same array back where there was nothing to fill, so this does not
      // re-render the page on every pass of an effect that watches `drafts`.
      return changed ? filled : current;
    });
  }, [units, drafts]);

  // Which card's write is out, so no second one starts under it. One at a
  // time: every write is the whole plan, and two in flight at once could land
  // in either order.
  const [savingId, setSavingId] = useState<string | null>(null);

  // Whether the send dialog is up. Saving a draft does not ask anything — it
  // goes nowhere — so only the one errand has a question in front of it.
  const [confirming, setConfirming] = useState(false);

  // The autosave below, as of the last render. The cleanup that runs it is
  // registered once, on the way in, and would otherwise be holding the cards
  // as they looked on the first render rather than the ones being left.
  const autosaveRef = useRef<(() => void) | undefined>(undefined);

  // Whether leaving was announced already — Save as draft says so itself, and
  // the autosave has no reason to say it a second time.
  const announced = useRef(false);

  // The screen going away is a save, not a discard — see `autosave`.
  // Registered once so it is a departure that runs it rather than a re-render.
  useEffect(() => () => autosaveRef.current?.(), []);

  // Read before the returns below, since a hook cannot sit behind one. The
  // plan counts as loading too: a screen opened on one has nothing to show
  // until it arrives. Not once there is a plan in hand — the query runs again
  // after the first save rewrites the URL, and the cards are not to be
  // replaced by a skeleton while it does.
  const loading =
    status === "loading" || (planStatus === "loading" && !stored);
  const phase = useLoadPhase(loading);

  const client = useMemo(
    () => (record ? toClientDetail(record) : undefined),
    [record],
  );

  const written = drafts.filter(isSupplementWritten);

  /**
   * Why the supplements cannot go to the client yet, or nothing if they can.
   *
   * Read as a sentence rather than a flag, the way the meal plan's is: a
   * button that has gone grey with no reason given reads as broken, and this
   * is what the tooltip says and what the press is answered with.
   *
   * Sending is what the client reads, so what it checks is that every card
   * says something they could act on — a name, how much to take, and the days
   * to take it on. A card still open as a form comes last, so one left blank
   * is told what to write rather than told to save nothing. And a card open
   * as a form holds what the backend has not been told — the same reason a
   * meal plan will not go with an option still open.
   */
  const sendBlockedReason = ((): string | undefined => {
    if (written.length === 0) {
      return "Write a supplement before sending.";
    }

    const noDosage = written.find((draft) => !draft.dosage.trim());
    if (noDosage) {
      return `${noDosage.name.trim()} needs a dose.`;
    }

    // A schedule with no days in it is not a schedule. It cannot happen by
    // accident — the toggles start with every day on — but a card can be left
    // with all seven turned off.
    const noDays = written.find((draft) => draft.days.length === 0);
    if (noDays) {
      return `Choose at least one day for ${noDays.name.trim()}.`;
    }

    const unsaved = written.find((draft) => !draft.saved);
    if (unsaved) {
      return `Save ${unsaved.name.trim()} before sending.`;
    }

    return undefined;
  })();

  /**
   * Whether Save as draft has anything to leave behind.
   *
   * Almost nothing holds a draft back, which is the point of one: a plan the
   * backend holds is a draft already, and a card left open as a form is
   * written by the leaving — see `autosave`. What stops it is a screen with
   * nothing the backend could take: no plan yet, and no card whole enough to
   * make one. That one is plainly disabled — the empty boxes are the reason,
   * and they are already on screen.
   */
  const nothingToFile = !stored && !drafts.some(isSupplementComplete);

  const update = (draft: SupplementDraft) =>
    setDrafts((current) =>
      current.map((one) => (one.id === draft.id ? draft : one)),
    );

  // Back to the Supplement list that opened this screen, not to the Meal one
  // the Plans tab opens on: every way out — Cancel, Save as draft and a send
  // that went — comes through here, and landing on a different list each time
  // reads as having been put somewhere rather than returned.
  const listPath =
    returnTo ?? clientDetailPath(clientId, "plans", "supplement");
  // Said to be a way back: the client's tab is no nearer the root than this
  // screen, and the paths alone would read leaving as opening something —
  // see `pageDirection`.
  const back = () => navigate(listPath, { state: transitionState("back") });

  /** What a refused request tells the user, in the words the API used. */
  const reportFailure = (what: string, reason: unknown) =>
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again.",
    );

  /**
   * The cards as the backend is about to hold them.
   *
   * A `PUT` is a replace, so what goes up is the whole plan, not the card
   * that was touched — and a card reopened with Modify is not to fall off the
   * plan for being mid-edit. So each card goes up as it stands where it is
   * saved or is the one being saved now, as the backend last held it where it
   * has been reopened, and not at all where the backend has never seen it
   * and nobody is saving it: that one is the browser's alone until its Save.
   */
  const cardsToStore = (
    cards: SupplementDraft[],
    saving: (card: SupplementDraft) => boolean = () => false
  ): PlanSupplement[] =>
    cards.flatMap((card) => {
      if (card.saved || saving(card)) return [card];
      const held = storedCards.current.get(card.id);
      return held ? [held] : [];
    });

  /**
   * Writes the plan — a `POST` the first time, a `PUT` after — and hands back
   * what the backend made of it.
   *
   * Every write comes through here, so the plan in hand and what it holds for
   * each card move on together, and only once the server has answered.
   */
  const writePlan = async (
    cards: PlanSupplement[]
  ): Promise<SupplementPlanRecord> => {
    const record = stored ? await save(stored.id, cards) : await create(cards);
    setStored(record);
    storedCards.current = new Map(cards.map((card) => [card.id, card]));
    return record;
  };

  /**
   * Saves a card: the plan goes up with it, and the card turns back into
   * something to read once the backend has it.
   *
   * On a blank screen this is the call that makes the plan, and the URL is
   * rewritten to name it — so a reload reopens the plan that was started
   * rather than a blank screen that would start another. The same plan is
   * written for every card after, whole.
   *
   * Awaited rather than fired off: a refusal leaves the card open as the
   * form it was written in, with everything typed into it still there to try
   * again with. Nothing is shown as saved unless the server said so.
   *
   * No toast on success. Writing a plan is one of these presses per card,
   * and a message for each would be a stack of notices confirming what the
   * card already shows: the form is gone and the answers are text. A failure
   * still speaks — that is the case the user cannot see for themselves.
   */
  const handleSaveCard = async (draft: SupplementDraft) => {
    if (savingId) return;
    setSavingId(draft.id);

    const cards = cardsToStore(drafts, (card) => card.id === draft.id);
    const firstWrite = !stored;

    try {
      const record = await writePlan(cards);
      filled.current = true;

      // Saved as the object that went up, and only that one: a card typed
      // into while the call was out is a different object, and is left open —
      // what is on screen is no longer what was stored.
      setDrafts((current) =>
        current.map((one) => (one === draft ? { ...one, saved: true } : one))
      );

      if (firstWrite) {
        navigate(createSupplementPath(clientId, record.id), {
          replace: true,
          state,
        });
      }
    } catch (reason) {
      reportFailure("Could not save the supplement", reason);
    } finally {
      setSavingId(null);
    }
  };

  /**
   * Takes a card off the plan.
   *
   * Only a card the backend holds is deleted there — as a `PUT` without it,
   * which is what a replace makes of a delete. The blank one it was never told
   * about is simply dropped, and nothing is asked of it. A refusal leaves the
   * card where it was, since it is still on the backend.
   */
  const handleRemove = async (draft: SupplementDraft) => {
    const remaining = drafts.filter((one) => one.id !== draft.id);

    if (storedCards.current.has(draft.id)) {
      if (savingId) return;
      setSavingId(draft.id);

      try {
        await writePlan(cardsToStore(remaining));
      } catch (reason) {
        reportFailure("Could not remove the supplement", reason);
        return;
      } finally {
        setSavingId(null);
      }

      // Said out loud, the way the meal builder says it: a card leaving the
      // page is not on its own an answer that the plan has changed.
      toast.success(
        "Supplement removed",
        `${draft.name.trim()} is no longer on the plan.`
      );
    }

    setDrafts(remaining);
  };

  /**
   * Writes back whatever was left open and unsaved, for a departure that
   * pressed nothing.
   *
   * Called as the screen closes — see the effect above — which is why none of
   * it waits on React: the page is already gone by the time the call comes
   * back. Coming back into the plan then finds the cards where they were left.
   *
   * Only a card whole enough to store, and only where it says something the
   * backend does not already hold: a card reopened with Modify and left as it
   * was is not a change, and a half-written one is not a supplement — that
   * one is lost with the screen, the same as a meal option with no dish on
   * it. The same rule `handleSaveCard` follows, applied to every open card at
   * once.
   *
   * It says so, on the screen that was navigated to — something was saved
   * without being asked for. Not where the departure announced itself: Save
   * as draft has already said it. A refusal is always said out loud: the
   * screen is gone, and the work would otherwise be lost without a word.
   *
   * Nothing runs at all where there is nothing to save, so leaving a plan
   * that was only read costs no calls. A plan that is only *to* be read has
   * no open card either, and one the workspace has no subscription for could
   * not be written anyway — see `saveGate`. Nor while a Save is already out:
   * that write holds what it was given, and a second beside it could land in
   * either order.
   */
  const autosave = () => {
    if (readOnly || saveGate.disabled || savingId) return;

    const pending = drafts.filter((draft) => {
      if (draft.saved || !isSupplementComplete(draft)) return false;
      const held = storedCards.current.get(draft.id);
      return !held || !isSameSupplement(draft, held);
    });
    if (pending.length === 0) return;

    const quietly = announced.current;

    writePlan(cardsToStore(drafts, isSupplementComplete))
      .then((record) => {
        if (quietly) return;
        toast.success(
          "Changes saved",
          `Your last changes to ${record.shortName} are saved.`
        );
      })
      .catch((reason) =>
        reportFailure("Could not save your last changes", reason)
      );
  };

  // Read on the way out, so it is the cards as they were left that get saved.
  autosaveRef.current = autosave;

  /**
   * Puts the plan away unfinished, which is the whole of what a draft is.
   *
   * Nothing is written here: every card saved is on the backend already, as
   * a draft, and a card still open as a form is written by the leaving — see
   * `autosave`. This is the one departure that says so itself, so the
   * autosave keeps quiet about it rather than following one message with
   * another. The same arrangement the meal builder's Save as draft makes.
   */
  const handleSaveDraft = () => {
    announced.current = true;
    toast.success(
      "Saved as draft",
      `Your supplements for ${client?.name ?? "this client"} are saved. They have not gone to the client.`
    );
    // Back to the screen the plan was started from — the client's Plans tab,
    // or the plan list. The same place sending it leaves for.
    back();
  };

  /**
   * Sends the stored plan to the addresses given.
   *
   * One call, and nothing written before it: a plan cannot be sent until
   * every card on it is saved — see `sendBlockedReason` — so by the time the
   * dialog is answered the backend already holds what is about to go. A plan
   * the client already has goes the same way, as it stands.
   *
   * A refusal leaves the dialog open on the addresses that were typed, so the
   * send can be tried again from what is already on screen. Only a send the
   * server confirmed leaves the screen.
   */
  const handleSend = async (emails: string[]) => {
    if (!stored || emails.length === 0) return;

    const what =
      written.length === 1
        ? written[0].name.trim()
        : `${written.length} supplements`;

    try {
      const message = await send(stored.id, emails);
      // The backend's own wording where it gave one — a change there reaches
      // the dietitian without this line being touched.
      toast.success(
        "Supplements sent",
        message ?? `${what} on their way to ${stored.clientName}.`,
      );
      back();
    } catch (reason) {
      reportFailure("Could not send the supplements", reason);
    }
  };

  /**
   * What the screen is called — by what it lets you do to the plan, except on
   * a plan just copied, which is named for where it came from: the cards are
   * the ones that were copied, and "Modify supplement" over them would read as
   * the plan they were copied from.
   */
  const title = readOnly
    ? "View supplement"
    : openedBlank
      ? "Create supplement"
      : duplicated
        ? "Duplicated supplement plan"
        : "Modify supplement";

  if (loading) {
    return (
      <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top">
        <PageHeader title="Create supplement" />
        <span role="status" className="sr-only">
          Loading this client…
        </span>
        <div className="mt-5 flex flex-col gap-4">
          <SkeletonCard phase={phase} className="h-[74px]" />
          <SkeletonCard phase={phase} className="h-[420px]" />
        </div>
      </div>
    );
  }

  // A failed load is not a missing client, and saying so would send someone
  // looking for a record that is sitting there behind an error.
  if (status !== "ready" || !client) {
    const failed = status === "failed";

    return (
      <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top">
        <PageHeader
          title={failed ? "Could not load this client" : "Client not found"}
          breadcrumbs={[
            { label: "My clients", to: ROUTES.myClients },
            { label: failed ? "Client unavailable" : "Client not found" },
          ]}
        />
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center card-surface border border-line bg-white">
          <EmptyState
            {...(failed
              ? failedEmptyState(
                  "We could not reach the server",
                  <Button size="sm" variant="secondary" onClick={reload}>
                    Try again
                  </Button>,
                )
              : notFoundEmptyState(
                  "That client no longer exists",
                  "They may have been deleted. Everyone else on your list is unaffected.",
                  <Link
                    to={ROUTES.myClients}
                    className={buttonClasses({
                      variant: "secondary",
                      size: "sm",
                    })}
                  >
                    Back to my clients
                  </Link>,
                ))}
          />
        </div>
      </div>
    );
  }

  // What the client is being seen for, if anything is on record — the pill
  // beside their name. The first of them: the strip has room for one, and the
  // rest are on the Details tab.
  const goal = client.goals[0];

  return (
    // The page scrolls, unlike the list screens: it is a column of forms that
    // grows with every supplement added, and there is no table inside it to
    // hand the leftover height to.
    <div className="min-h-full md:h-full md:overflow-y-auto custom-scrollbar-overlay p-page pt-page-top">
      <PageHeader
        title={title}
        // The trail leads back the way the screen was reached: through the
        // client when it was their Plans tab, through Create plan when it was
        // that list.
        breadcrumbs={[
          ...(returnTo
            ? [{ label: "Create plan", to: returnTo }]
            : [
                { label: "My clients", to: ROUTES.myClients },
                { label: client.name, to: listPath },
              ]),
          { label: title },
        ]}
      />

      <div className="mt-5 flex flex-col gap-4 pb-8">
        {/* Who this is for, and the two ways out of the screen. Pinned above
            the cards rather than in the page header, because it is about the
            supplements below it rather than about the page. */}
        <section className="flex flex-wrap items-center justify-between gap-4 card-surface border border-line bg-white px-5 sm:px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#D8E9FF] text-[14px] font-medium text-[#20476F]"
            >
              {initialOf(client.name)}
            </span>
            <span className="truncate text-[15px] 3xl:text-[17px] font-medium text-ink">
              {client.name}
            </span>
            {goal && (
              <span className="shrink-0 rounded-full bg-[#EFFBFD] px-2.5 py-1 text-[12px] text-[#00424B]">
                {goal}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* A link in weight rather than a second filled button: of the two
                ways out, sending is the one the screen is for.

                Nothing to put away on a plan the client has: pressing this
                would file an issued plan back as a draft. */}
            {!readOnly && (
              <button
                type="button"
                // An empty screen stays plainly disabled — there is nothing to
                // explain. A workspace with no plan behind it has something to do
                // about it, so that press is turned back to the reason rather
                // than swallowed. See `lockedProps`, and the bin on a meal slot,
                // which makes the same pair.
                {...(nothingToFile
                  ? { disabled: true, title: "Nothing to save yet" }
                  : lockedProps(saveGate, handleSaveDraft))}
                // The meal plan's own Save as draft, down to the padding: the
                // hover is a soft pill the label sits inside, rather than the
                // tint hugging the text that `px-1` left it as. Both screens
                // stand this button beside a filled Send, and two ways out that
                // light up differently read as two different kinds of button.
                className={`rounded-[6px] px-6 py-2.5 max-md:py-3.5 text-sm 3xl:text-base transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
                  nothingToFile || saveGate.disabled
                    ? "cursor-not-allowed text-ink-subtle"
                    : "text-ink hover:text-brand hover:bg-brand-soft"
                }`}
              >
                Save as draft
              </button>
            )}
            <Button
              size="md"
              // Only a supplement the client could act on goes out. Greyed
              // rather than hidden: the button is what the screen is for, and
              // one that vanished until a name was typed would read as a
              // screen with no way out.
              //
              // And only a workspace with a plan behind it sends anything at
              // all. That answer leads, the way it does on a meal plan: a
              // lapsed subscription is the bigger fact, and the one there is
              // something to do about.
              //
              // A plan already with the client has nothing holding it back —
              // it went once as it stands — so only the gate applies.
              disabled={saveGate.disabled || (!readOnly && !!sendBlockedReason)}
              title={
                saveGate.title ?? (readOnly ? undefined : sendBlockedReason)
              }
              // Asked before it goes, the way a meal plan is: the addresses
              // are collected in the dialog, and what is about to be sent is
              // read back there first.
              onClick={() => setConfirming(true)}
              // Pressed anyway, which is what someone does when a button has
              // gone grey and they cannot see why. The reason is said out loud
              // rather than left to a tooltip nobody hovered for.
              onDisabledClick={
                saveGate.disabled
                  ? saveGate.onDisabledClick
                  : () =>
                      toast.warning(
                        "These supplements are not ready to send",
                        sendBlockedReason,
                      )
              }
            >
              {readOnly ? "Send again" : "Save & send"}
            </Button>
          </div>
        </section>

        {drafts.map((draft, index) => (
          <SupplementCard
            key={draft.id}
            draft={draft}
            index={index}
            units={units}
            meals={meals}
            onChange={update}
            onSave={() => void handleSaveCard(draft)}
            saving={savingId === draft.id}
            gate={saveGate}
            readOnly={readOnly}
            onRemove={
              drafts.length > 1 ? () => void handleRemove(draft) : undefined
            }
          />
        ))}

        {/* The next card, named for the number it will take — so the button
            says what pressing it produces rather than "Add another". Not on a
            plan the client has: there is nothing to add a card to. */}
        {!readOnly && (
          <button
            type="button"
            onClick={() =>
              setDrafts((current) => [...current, createSupplementDraft()])
            }
            className="flex w-full items-center justify-center gap-2 rounded-md border border-[#88E2ED] bg-white py-4 text-[14px] 3xl:text-[15px] font-medium text-brand transition-colors hover:bg-[#F8FEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <Plus className="h-4 w-4" />
            Add supplement {drafts.length + 1}
          </button>
        )}
      </div>

      {confirming && (
        <SendSupplementModal
          clientName={client.name}
          // Off the record already in hand — this screen loaded the client, so
          // there is nothing to fetch for it.
          clientEmail={client.email}
          supplements={written}
          // The plan the backend holds, which is what Download renders. Always
          // one by now: a send waits until every card is saved, and the first
          // save is what makes the plan.
          plan={stored}
          onClose={() => setConfirming(false)}
          onSend={handleSend}
        />
      )}
    </div>
  );
};

export default CreateSupplementPage;
